import { useQuery } from '@tanstack/react-query';

import { type Board } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { publicBoardsFetcher } from '~/swr-hooks/gallery/galleryView/utilsCollection';

export const PUBLIC_SHOWCASE_NAV_PAGE_BOARDS = 'PUBLIC_SHOWCASE_NAV_PAGE_BOARDS';

export type PublicShowcaseNavPageBoardsKeyParams = {
  /** The page's own collection, or `null` for the ROOT page (the share link's own collection). */
  pageCollectionId: string | null;
  shortId: string;
};

export type PublicShowcaseNavPageBoardsKey = [
  typeof PUBLIC_SHOWCASE_NAV_PAGE_BOARDS,
  PublicShowcaseNavPageBoardsKeyParams,
];

export const getPublicShowcaseNavPageBoardsKey = ({
  pageCollectionId,
  shortId,
}: PublicShowcaseNavPageBoardsKeyParams): PublicShowcaseNavPageBoardsKey => [
  PUBLIC_SHOWCASE_NAV_PAGE_BOARDS,
  { pageCollectionId, shortId },
];

export type UsePublicShowcaseNavPageBoardsParams = {
  /** See the key params — `null` is the root page, which is the endpoint's default scope. */
  pageCollectionId: string | null;
};

/** Module scope so a not-yet-loaded page hands the same array to every render. */
const NO_BOARDS: Board[] = [];

/**
 * The boards one public-nav page holds — the rows inside its dropdown, and the `total` that decides
 * whether the page shows a boards chevron at all.
 *
 * Live `/a` ShortUrl.Boards.list. Edit + Preview supply `useShowcaseEditorNavPageBoards` on the
 * layout contract instead — this hook never branches into workspace APIs.
 *
 * Deliberately a single first page rather than drained pagination: `total` is the authoritative
 * count straight off the response, so draining would buy nothing but requests. A page with more
 * boards than the first page therefore shows the first page of rows (the chevron/count still reflect
 * the full `total`).
 *
 * MOUNTING is the gate — there is no `enabled` flag beyond a present shortId. Each non-external nav
 * entry mounts one of these, and the shared query key dedupes the desktop bar and the mobile menu
 * (both mounted below the medium breakpoint) onto one request per page.
 */
export const usePublicShowcaseNavPageBoards = ({ pageCollectionId }: UsePublicShowcaseNavPageBoardsParams) => {
  const { shortId } = useShortIdContext();

  const { data, isPending } = useQuery({
    queryKey: getPublicShowcaseNavPageBoardsKey({
      pageCollectionId,
      shortId,
    }),
    queryFn: () =>
      publicBoardsFetcher({
        cursor: null,
        params: {},
        shortId,
        subPageCollectionId: pageCollectionId ?? undefined,
      }),
    enabled: !!shortId,
  });

  return {
    boardCount: data?.total ?? 0,
    boards: data?.data ?? NO_BOARDS,
    isLoadingBoards: isPending,
  };
};
